import { io, Socket } from 'socket.io-client';

export type BlockchainEvent =
  | 'vote:recorded'
  | 'election:created'
  | 'election:status_changed'
  | 'tallying:started'
  | 'tallying:completed';

export interface VoteRecordedEvent {
  electionId: string;
  blockNumber: number;
  timestamp: Date;
  message: string;
  voteHash?: string;
}

export interface ElectionCreatedEvent {
  electionId: string;
  title: string;
  manager: string;
  message: string;
}

export interface ElectionStatusChangedEvent {
  electionId: string;
  oldStatus: string;
  newStatus: string;
  timestamp: Date;
  message: string;
}

export interface TallyingEvent {
  electionId: string;
  totalVotesCast: number;
  timestamp: Date;
  message: string;
}

export type EventCallback<T = any> = (data: T) => void;

/**
 * WebSocketService - Manages WebSocket connections to the backend
 * Provides real-time notifications for blockchain events
 */
class WebSocketService {
  private socket: Socket | null = null;
  private token: string | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private eventHandlers: Map<string, Set<EventCallback>> = new Map();
  private isConnected = false;

  /**
   * Connect to the WebSocket server
   */
  connect(token: string): void {
    if (this.socket?.connected) {
      console.log('🔌 WebSocket already connected');
      return;
    }

    this.token = token;
    const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:3000';

    console.log('🔌 Connecting to WebSocket server...');

    this.socket = io(backendUrl, {
      auth: {
        token,
      },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: this.maxReconnectAttempts,
    });

    this.setupEventHandlers();
  }

  /**
   * Setup WebSocket event handlers
   */
  private setupEventHandlers(): void {
    if (!this.socket) return;

    // Connection events
    this.socket.on('connect', () => {
      console.log('✅ WebSocket connected');
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.emit('connection:established', { timestamp: new Date() });
    });

    this.socket.on('connected', (data) => {
      console.log('✅ WebSocket authenticated:', data);
    });

    this.socket.on('disconnect', (reason) => {
      console.log('🔌 WebSocket disconnected:', reason);
      this.isConnected = false;
      this.emit('connection:lost', { reason, timestamp: new Date() });
    });

    this.socket.on('connect_error', (error) => {
      console.error('❌ WebSocket connection error:', error);
      this.reconnectAttempts++;

      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error('❌ Max reconnection attempts reached');
        this.emit('connection:failed', {
          error: 'Failed to connect after maximum attempts',
          timestamp: new Date(),
        });
      }
    });

    // Blockchain events
    this.socket.on('vote:recorded', (data: VoteRecordedEvent) => {
      console.log('📥 Vote recorded:', data);
      this.emit('vote:recorded', data);
    });

    this.socket.on('election:created', (data: ElectionCreatedEvent) => {
      console.log('📥 Election created:', data);
      this.emit('election:created', data);
    });

    this.socket.on('election:status_changed', (data: ElectionStatusChangedEvent) => {
      console.log('📥 Election status changed:', data);
      this.emit('election:status_changed', data);
    });

    this.socket.on('tallying:started', (data: TallyingEvent) => {
      console.log('📥 Tallying started:', data);
      this.emit('tallying:started', data);
    });

    this.socket.on('tallying:completed', (data: TallyingEvent) => {
      console.log('📥 Tallying completed:', data);
      this.emit('tallying:completed', data);
    });

    // Subscription confirmations
    this.socket.on('subscribed', (data) => {
      console.log('📬 Subscribed:', data);
      this.emit('subscribed', data);
    });

    this.socket.on('unsubscribed', (data) => {
      console.log('📭 Unsubscribed:', data);
      this.emit('unsubscribed', data);
    });

    // Server events
    this.socket.on('server:shutdown', (data) => {
      console.warn('⚠️  Server shutting down:', data);
      this.emit('server:shutdown', data);
    });

    // Error handling
    this.socket.on('error', (error) => {
      console.error('❌ WebSocket error:', error);
      this.emit('error', error);
    });
  }

  /**
   * Subscribe to updates for a specific election
   */
  subscribeToElection(electionId: string): void {
    if (!this.socket?.connected) {
      console.warn('⚠️  Cannot subscribe: WebSocket not connected');
      return;
    }

    console.log(`📬 Subscribing to election ${electionId}`);
    this.socket.emit('subscribe:election', electionId);
  }

  /**
   * Unsubscribe from election updates
   */
  unsubscribeFromElection(electionId: string): void {
    if (!this.socket?.connected) {
      return;
    }

    console.log(`📭 Unsubscribing from election ${electionId}`);
    this.socket.emit('unsubscribe:election', electionId);
  }

  /**
   * Subscribe to admin channel (admin only)
   */
  subscribeToAdminChannel(): void {
    if (!this.socket?.connected) {
      console.warn('⚠️  Cannot subscribe: WebSocket not connected');
      return;
    }

    console.log('👑 Subscribing to admin channel');
    this.socket.emit('subscribe:admin');
  }

  /**
   * Register an event handler
   */
  on<T = any>(event: string, callback: EventCallback<T>): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event)!.add(callback as EventCallback);
  }

  /**
   * Unregister an event handler
   */
  off<T = any>(event: string, callback: EventCallback<T>): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.delete(callback as EventCallback);
    }
  }

  /**
   * Emit an event to registered handlers
   */
  private emit(event: string, data: any): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error(`Error in event handler for ${event}:`, error);
        }
      });
    }
  }

  /**
   * Send a ping to check connection health
   */
  ping(): void {
    if (!this.socket?.connected) {
      console.warn('⚠️  Cannot ping: WebSocket not connected');
      return;
    }

    this.socket.emit('ping');
  }

  /**
   * Disconnect from the WebSocket server
   */
  disconnect(): void {
    if (!this.socket) {
      return;
    }

    console.log('🔌 Disconnecting from WebSocket server...');
    this.socket.disconnect();
    this.socket = null;
    this.isConnected = false;
    this.token = null;
    this.eventHandlers.clear();
  }

  /**
   * Get connection status
   */
  getConnectionStatus(): {
    connected: boolean;
    reconnectAttempts: number;
  } {
    return {
      connected: this.isConnected,
      reconnectAttempts: this.reconnectAttempts,
    };
  }

  /**
   * Reconnect to the WebSocket server
   */
  reconnect(): void {
    if (this.token) {
      this.disconnect();
      this.connect(this.token);
    } else {
      console.error('❌ Cannot reconnect: No authentication token available');
    }
  }
}

// Export singleton instance
export const websocketService = new WebSocketService();
